'use client';
import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/site/ui';

/* shadcn-style Select on Radix. Replaces native <select>: styled popover,
   keyboard nav, and the `name` prop renders a hidden field so FormData still
   works in server-action forms. Trigger matches the field.tsx control pill. */

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      'group flex h-11 w-full items-center justify-between gap-2 rounded-field border border-input bg-white px-3.5 text-sm text-foreground outline-none transition-[border-color,box-shadow] duration-150 ease-spring hover:border-neutral-300',
      'data-[placeholder]:text-neutral-400 focus-visible:border-medlux focus-visible:shadow-field-focus',
      'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground [&>span]:truncate',
      className,
    )}
    {...props}
  >
    {children}
    <SelectPrimitive.Icon asChild>
      <span className="pointer-events-none shrink-0 text-muted-foreground transition-transform duration-200 ease-spring group-data-[state=open]:rotate-180 motion-reduce:transition-none">
        <Icon name="arrow-down-01" size={16} />
      </span>
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = 'SelectTrigger';

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      className={cn(
        // `medlux-menu` (globals.css): 160ms scale+fade in, 100ms out, fade-only under reduced
        // motion. The old animate-in/out classes needed a plugin that is not installed.
        'medlux-menu relative z-50 max-h-72 min-w-[8rem] overflow-hidden rounded-lg border border-border bg-white text-foreground shadow-lg origin-[var(--radix-select-content-transform-origin)]',
        position === 'popper' && 'data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
        className,
      )}
      {...props}
    >
      <SelectPrimitive.Viewport
        className={cn(
          'p-1.5',
          position === 'popper' && 'w-full min-w-[var(--radix-select-trigger-width)]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = 'SelectContent';

export const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-xs font-medium text-muted-foreground', className)}
    {...props}
  />
));
SelectLabel.displayName = 'SelectLabel';

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    /** `end`: the check sits on the right, for items that start with an avatar or icon. */
    indicator?: 'start' | 'end';
  }
>(({ className, children, indicator = 'start', ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-pointer select-none items-center rounded-md py-2 text-sm outline-none',
      indicator === 'end' ? 'pl-2 pr-8' : 'pl-8 pr-2',
      'focus:bg-medlux-soft focus:text-medlux-soft-foreground data-[state=checked]:font-semibold',
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <span className={cn('absolute flex items-center text-medlux', indicator === 'end' ? 'right-2' : 'left-2')}>
      <SelectPrimitive.ItemIndicator>
        <Icon name="tick-02" size={15} />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
));
SelectItem.displayName = 'SelectItem';

export const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator ref={ref} className={cn('my-1 h-px bg-border', className)} {...props} />
));
SelectSeparator.displayName = 'SelectSeparator';
